import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Mail, CheckCircle, XCircle, ExternalLink, Loader2, Unlink, UserX, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Check for success/error in URL params
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'true') {
      setMessage({ type: 'success', text: 'Gmail connected successfully!' });
      // Clear URL params
      window.history.replaceState({}, '', '/settings/email');
    } else if (error) {
      setMessage({ type: 'error', text: `Connection failed: ${error}` });
      window.history.replaceState({}, '', '/settings/email');
    }
    
    fetchGmailStatus();
  }, [searchParams]);

  const fetchGmailStatus = async () => {
    try {
      const res = await fetch(`${API}/api/gmail/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setGmailStatus(data);
    } catch (err) {
      console.error('Error fetching Gmail status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/oauth/gmail/login`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        setMessage({ type: 'error', text: 'Failed to get authorization URL' });
        setConnecting(false);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to initiate connection' });
      setConnecting(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? You will no longer be able to send/receive emails through the CRM.')) {
      return;
    }
    
    setDisconnecting(true);
    try {
      await fetch(`${API}/api/gmail/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setGmailStatus({ connected: false });
      setMessage({ type: 'success', text: 'Gmail disconnected successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to disconnect Gmail' });
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="email-settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-[#1A1A1A] tracking-tight">Email Settings</h1>
        <p className="text-[#6B7280] mt-1">Manage your email integration for seamless communication</p>
      </div>

      {message && (
        <div 
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          data-testid="status-message"
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          {message.text}
        </div>
      )}

      <Card className="border-[#E5E7EB] shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-medium">Gmail Integration</CardTitle>
              <CardDescription>Connect your Gmail to send and receive emails directly in the CRM</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {gmailStatus?.connected ? (
            <>
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Connected</p>
                  <p className="text-sm text-green-700">{gmailStatus.email}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-medium text-[#1A1A1A]">What you can do:</h3>
                <ul className="space-y-2 text-sm text-[#6B7280]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Send emails to contacts with your signature
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    View email threads and replies from contacts
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Track all email conversations in one place
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-[#E5E7EB]">
                <Button 
                  variant="outline"
                  onClick={handleDisconnectGmail}
                  disabled={disconnecting}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="disconnect-gmail-btn"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4 mr-2" />
                  )}
                  Disconnect Gmail
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <h3 className="font-medium text-[#1A1A1A]">Connect Gmail to:</h3>
                <ul className="space-y-2 text-sm text-[#6B7280]">
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#C9A227]" />
                    Send emails directly from contact pages
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#C9A227]" />
                    See email replies without leaving the CRM
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#C9A227]" />
                    Your signature auto-appends to all emails
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={handleConnectGmail}
                disabled={connecting}
                className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white"
                data-testid="connect-gmail-btn"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Connect Gmail Account
              </Button>
              
              <p className="text-xs text-[#9CA3AF] text-center">
                You'll be redirected to Google to authorize access. We only request permissions to read and send emails.
              </p>
            </>
          )}
        </CardContent>
      </Card>
      <SuppressionsPanel token={token} />
    </div>
  );
}

// Per-sender unsubscribe suppression list — recipients who opted out via the
// List-Unsubscribe header. One-click "Re-subscribe" removes the entry so
// the user can email them again.
function SuppressionsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/email/suppressions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setItems(data.suppressions || []);
    } catch (e) {
      // silent — page still renders
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResubscribe = async (email) => {
    if (!window.confirm(`Re-subscribe ${email}? They'll be able to receive your emails again.`)) return;
    setRemoving(email);
    try {
      const r = await fetch(
        `${API}/api/email/suppressions/${encodeURIComponent(email)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        setItems(prev => prev.filter(i => i.recipient_email !== email));
      }
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="border-[#E5E7EB] shadow-sm mt-6" data-testid="suppressions-panel">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#FEF3C7] rounded-full">
            <UserX className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <CardTitle className="text-xl font-medium">Suppressions</CardTitle>
            <CardDescription>
              Recipients who unsubscribed. They won't receive future emails until re-subscribed.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#6B7280]" data-testid="suppressions-empty">
            No suppressions. All your recipients are subscribed.
          </p>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {items.map(item => (
              <li
                key={item.recipient_email}
                className="flex items-center justify-between py-3"
                data-testid={`suppression-row-${item.recipient_email}`}
              >
                <div>
                  <div className="text-sm font-medium text-[#1A1A1A]">{item.recipient_email}</div>
                  <div className="text-xs text-[#9CA3AF]">
                    Unsubscribed {item.unsubscribed_at ? new Date(item.unsubscribed_at).toLocaleDateString() : 'recently'}
                    {item.source ? ` · via ${item.source.replace(/_/g, ' ')}` : ''}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => handleResubscribe(item.recipient_email)}
                  disabled={removing === item.recipient_email}
                  data-testid={`resubscribe-${item.recipient_email}`}
                >
                  {removing === item.recipient_email ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <><RotateCcw className="w-4 h-4 mr-1" /> Re-subscribe</>
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
